import React, { useMemo } from 'react'
import { CobuStack, CobuText, CobuImage, CobuButton, CobuTextAvatar } from '@cobu/ui'
import { Strings } from '../../constants/Strings';
import Bubble from '../../assets/icons/Bubble';

type Props = {
  user: any
  currentUser: any
  onManageProfile?: () => void
  onMessage?: () => void
  onMoreOptions?: () => void
  onProfileImagePress?: () => void
  canMessage?: boolean
}

export default function PublicProfileInfoSection({
  user,
  currentUser,
  onManageProfile,
  onMessage,
  onMoreOptions,
  onProfileImagePress,
  canMessage = true,
}: Props) {
  const isOwnProfile = !!currentUser?.uuid && (currentUser.uuid === (user?.id || user?.uuid))
  const strings = Strings.get();
  const displayName: string = user?.displayName || 'User'
  const initials: string = useMemo(() => {
    const parts = displayName.split(' ').filter(Boolean)
    if (parts.length === 0) return 'UU'
    const letters = parts.map((p: string) => p[0]).join('').slice(0, 2)
    return letters.toUpperCase()
  }, [displayName])

  const membershipAge: string = useMemo(() => {
    const createdAt: string | undefined = user?.createdAt
    if (!createdAt) return ''
    const created = new Date(createdAt).getTime()
    const now = Date.now()
    const diffMs = Math.max(0, now - created)
    const oneMinute = 60 * 1000
    const oneHour = 60 * oneMinute
    const oneDay = 24 * oneHour
    const oneMonth = 30 * oneDay
    const oneYear = 365 * oneDay
    const ma = strings.publicProfile.membershipAge
    if (diffMs < oneHour) return ma.anHour
    if (diffMs < oneDay) {
      const hours = Math.round(diffMs / oneHour)
      return `${hours} ${hours === 1 ? ma.hour : ma.hours}`
    }
    if (diffMs < oneMonth) {
      const days = Math.round(diffMs / oneDay)
      return `${days} ${days === 1 ? ma.day : ma.days}`
    }
    if (diffMs < oneYear) {
      const months = Math.round(diffMs / oneMonth)
      return `${months} ${months === 1 ? ma.month : ma.months}`
    }
    const years = Math.round(diffMs / oneYear)
    return `${years} ${years === 1 ? ma.year : ma.years}`
  }, [user?.createdAt, strings.publicProfile.membershipAge])

  return (
    <CobuStack bg="$background" pt="$6" pb="$4" px="$4" style={{ zIndex: 10 }}>
      <CobuStack layout="horizontal" ai="center" gap="$4" mt={-30} mb="$4">
        <CobuStack 
          w="$20" 
          h="$20" 
          br="$full" 
          ov="hidden" 
          bg="$white" 
          p="$1" 
          onPress={user?.profileImage ? onProfileImagePress : undefined}
          style={{
            boxShadow: '0 2px 4px var(--blackTransparent50)',
            cursor: user?.profileImage && onProfileImagePress ? 'pointer' : 'default'
          }}
        >
          {user?.profileImage ? (
            <CobuImage 
              src={user.profileImage} 
              width="100%" 
              height="100%" 
              borderRadius="50%"
              objectFit="cover"
            />
          ) : (
            <CobuTextAvatar 
              name={displayName}
              uuid={user?.uuid || user?.id || 'default'}
              size="100%"
              fontSize={'$6' as any}
            />
          )}
        </CobuStack>

        <CobuStack gap="$1" f={1}>
          <CobuText variant="heading" fontWeight="bold" fontSize={'$4' as any} color="$cobuDarkBlue" numberOfLines={1 as any}>
            {displayName}
          </CobuText>
          {user?.building?.name ? (
            <CobuText variant="body" color="$cobuDarkBlue50" fontSize={'$2' as any} numberOfLines={1 as any}>
              {user.building.name}
            </CobuText>
          ) : null}
        </CobuStack>

        {!isOwnProfile && onMoreOptions ? (
          <CobuStack onPress={onMoreOptions} role="button" tabIndex={0} ai="center" jc="center" style={{ width: 40, height: 40, cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
              <circle cx="5" cy="12" r="2" fill="var(--cobuDarkBlue)" />
              <circle cx="12" cy="12" r="2" fill="var(--cobuDarkBlue)" />
              <circle cx="19" cy="12" r="2" fill="var(--cobuDarkBlue)" />
            </svg>
          </CobuStack>
        ) : null}
      </CobuStack>

      <CobuStack layout="horizontal" ai="center" gap="$3" w="100%" mb="$4">
        <CobuStack f={1}>
          {isOwnProfile && onManageProfile ? (
            <CobuButton
              onPress={onManageProfile}
              color="$white"
              fontWeight="500"
              bg="$cobuTeal"
              py="$2"
              px="$2"
              fontSize={'$2' as any}
              br="$5"
              minH="$10"
              h="auto"
              f={1}
              fs={1 as any}
            >
              <CobuText
                variant="body"
                fos="$2"
                col="$white"
                fow="500"
              >
                {strings.profile.components.managePublicProfile.publicProfile.header.managePublicProfile}
              </CobuText>
            </CobuButton>
          ) : !isOwnProfile && canMessage && onMessage ? (
            <CobuButton
              onPress={onMessage}
              color="$white"
              fontWeight="500"
              bg="$cobuPink"
              py="$2"
              px="$3"
              fontSize={'$2' as any}
              br="$5"
              minH="$10"
              h="auto"
              f={1}
              fs={1 as any}
            >
              <CobuStack layout="horizontal" ai="center" gap="$2">
                <Bubble size={20} color="#FFFFFF" />
                <CobuText
                  variant="body"
                  color="$white"
                  fontWeight="bold"
                  fontSize={'$2' as any}
                >
                  {strings.profile.components.managePublicProfile.publicProfile.header.message}
                </CobuText>
              </CobuStack>
            </CobuButton>
          ) : null}
        </CobuStack>

        {user?.createdAt ? (
          <CobuStack layout="horizontal" ai="center" gap="$2" bg="$cobuTopaz10" px="$3" py="$2" br="$5" minH="$10" h="auto">
            <CobuStack w="$4" h="$4" ai="center" jc="center" br="$full" style={{ backgroundColor: 'transparent' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                <path d="M12 8v5l4 2" fill="none" stroke="var(--cobuTeal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="9" fill="none" stroke="var(--cobuTeal)" strokeWidth="2" />
              </svg>
            </CobuStack>
            <CobuText variant="caption" color="$cobuDarkBlue" fontSize={'$2' as any}>
              <CobuText variant="caption" color="$cobuDarkBlue" fontWeight="bold" fontSize={'$2' as any}>
                {membershipAge}{' '}
              </CobuText>
              {strings.publicProfile.membershipAge.onCobu}
            </CobuText>
          </CobuStack>
        ) : null}
      </CobuStack>

      {user?.bio ? (
        <CobuStack px="$1" bg="$background" br="$3">
          <CobuText variant="subheading" fontWeight="bold" fontSize={'$5' as any} color="$cobuDarkBlue">{strings.profile.components.managePublicProfile.publicProfile.about.title}</CobuText>
          <CobuText variant="body" color="$cobuDarkBlue" fontSize={'$3' as any}>{user.bio}</CobuText>
        </CobuStack>
      ) : null}
    </CobuStack>
  )
}


